import { generateClient } from 'aws-amplify/api';
import { FEATURE_FLAGS } from '../config/featureFlags';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};

class PresenceService {
  constructor() {
    this.userStatuses = new Map();
    this.heartbeatInterval = null;
    this.activityTimeout = null;
    this.isActive = true;
    this.isTracking = false;
    this.lastActivity = Date.now();
  }

  // Update user online status
  async updateStatus(userId, status = 'online') {
    // Skip if presence is disabled
    if (!FEATURE_FLAGS.ENABLE_PRESENCE) {
      this.userStatuses.set(userId, { status, lastSeen: new Date() });
      return;
    }
    
    const currentStatus = this.userStatuses.get(userId);
    
    // Skip update if status hasn't changed and last update was less than 30 seconds ago
    if (currentStatus && currentStatus.status === status && 
        Date.now() - currentStatus.lastSeen.getTime() < 30000) {
      return;
    }

    try {
      await getClient().graphql({
        query: `
          mutation UpdateUserPresence($input: UpdateUserPresenceInput!) {
            updateUserPresence(input: $input) {
              id
              userID
              status
              lastSeen
            }
          }
        `,
        variables: {
          input: {
            id: `presence-${userId}`,
            userID: userId,
            status,
            lastSeen: new Date().toISOString()
          }
        }
      });
      this.userStatuses.set(userId, { status, lastSeen: new Date() });
    } catch (error) {
      // Fallback: just update local status
      this.userStatuses.set(userId, { status, lastSeen: new Date() });
    }
  }

  // Get user status
  getUserStatus(userId) {
    if (!userId) return 'offline';
    
    const userStatus = this.userStatuses.get(userId);
    if (!userStatus) return 'offline';
    
    const timeDiff = Date.now() - userStatus.lastSeen.getTime();
    if (timeDiff > 300000) return 'offline'; // 5 minutes
    if (timeDiff > 60000) return 'away'; // 1 minute
    return userStatus.status;
  }

  // Start presence tracking
  startTracking(userId) {
    // Skip if presence is disabled
    if (!FEATURE_FLAGS.ENABLE_PRESENCE) {
      console.log('Presence tracking disabled via feature flag');
      return;
    }
    
    if (this.isTracking) return;
    this.isTracking = true;
    
    this.updateStatus(userId, 'online');
    
    // Send heartbeat every 5 minutes to reduce API calls
    this.heartbeatInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - this.lastActivity;
      const status = timeSinceActivity > 300000 ? 'away' : 'online';
      this.updateStatus(userId, status);
    }, 300000);

    // Track user activity with throttling
    const handleActivity = this.throttle(() => {
      this.lastActivity = Date.now();
      this.isActive = true;
      if (this.activityTimeout) clearTimeout(this.activityTimeout);
      this.activityTimeout = setTimeout(() => {
        this.isActive = false;
      }, 300000); // 5 minutes
    }, 5000); // Throttle to once per 5 seconds

    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle page visibility
    const handleVisibilityChange = () => {
      if (document.hidden) {
        this.updateStatus(userId, 'away');
      } else {
        this.updateStatus(userId, 'online');
        this.lastActivity = Date.now();
        this.isActive = true;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.updateStatus(userId, 'offline');
    });
  }

  // Throttle function to limit function calls
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // Stop presence tracking
  stopTracking(userId) {
    this.isTracking = false;
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }
    this.updateStatus(userId, 'offline');
  }

  // Subscribe to presence updates (fallback implementation)
  subscribeToPresence(userIds, callback) {
    // Skip if presence is disabled
    if (!FEATURE_FLAGS.ENABLE_PRESENCE) {
      return () => {};
    }
    
    if (!Array.isArray(userIds)) return () => {};
    
    // Polling frequency set to 5 minutes
    const interval = setInterval(() => {
      userIds.forEach(userId => {
        const status = this.getUserStatus(userId);
        callback({ userId, status, lastSeen: new Date() });
      });
    }, 300000);

    return () => clearInterval(interval);
  }

  // Subscribe to user presence (alias for compatibility)
  subscribeToUserPresence(userIds, callback) {
    return this.subscribeToPresence(userIds, callback);
  }

  // Update user presence (alias for compatibility)
  updateUserPresence(userId, status) {
    return this.updateStatus(userId, status);
  }
}

export const presenceService = new PresenceService();